import React from "react";
import _ from "underscore";
import { BookMarked, ChevronDown, ExternalLink, Trash2, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import RequestSearchButton from "../tutoring_job/buttons/search";
import VerifiedJobButton from "../tutoring_job/buttons/verified_job";
import RequestPlacement from "../../request_placement/show";
import LessonPlanLessonSetup from "./setup/show";
import LessonPlanNotes from "./notes/show";
import LessonPlanPricing from "./pricing/show";
import LessonPlanTutors from "./tutors/show";
import TutoringJobPositions from "../tutoring_job/positions/index";
import TutorSearcher from "../../tutor_searcher/show";
import PendingTutorProposals from "../tutoring_job/tutor_search/results/pending_tutor_proposals/index";
import LessonPlanMeetings from "./meetings/show";
import store from "../../../state/store";

class EnquiryLessonPlan extends React.Component {
  onTabClick = (value) => {
    if (value === "placement") {
      store.emit("tutoring_jobs:show", this.props.lesson_plan.tutoring_job.id);
    } else if (value === "tutors") {
      store.emit(
        "tutoring_job:tutor_results:index",
        this.props.lesson_plan.tutoring_job.id,
      );
    } else if (value === "lesson_pricing") {
      const state = store.get();
      state.set({ change_applied: false });

      store.emit("lesson_plan:lesson_orders:index", this.props.lesson_plan.id);
    }

    store.emit('ui:lesson_plan:tab', value);
  };

  onRemove = () => {
    store.emit(
      "enquiry:lesson_plans:destroy",
      this.props.enquiry_id,
      this.props.lesson_plan.id,
    );
  };

  onStartSearch = () => {
    store.emit("tutoring_job:tutor_search:start", this.props.tutoring_job.id);
  };

  onStopSearch = () => {
    store.emit("tutoring_job:tutor_search:stop", this.props.tutoring_job.id);
  };

  onRequestPlacement = () => {
    const position_ids = (this.props.tutoring_job.tutoring_job_positions || []).map((p) => p.id);
    store.emit("ui:request_placement:open", this.props.tutoring_job.id, position_ids);
  };

  render() {
    const tab = this.props.ui?.enquiry?.lesson_plan_tab || "lesson_setup";

    const tabOptions = [
      { value: "lesson_setup", name: "Setup" },
      { value: "lesson_pricing", name: "Pricing" },
      { value: "tutors", name: "Tutors" },
      { value: "placement", name: "Placement" },
      { value: "notes", name: "Notes" },
    ];

    const job_alert_requisites = _.select(
      this.props.lesson_plan.job_alert_requisites,
      function (requisite) {
        return requisite.met === false;
      },
    );

    const pending_tutor_proposals = _.select(
      this.props.tutor_proposals,
      function (tutor_proposal) {
        return !_.isFinite(tutor_proposal.id);
      },
    );

    return (
      <div className="flex flex-1 flex-col gap-4 bg-muted p-4">
        <Card className="gap-3 px-6 py-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="gap-2">
              <BookMarked className="size-4" />
              {this.props.lesson_plan.subject_category_name
                ? `${this.props.lesson_plan.subject_level_name} ${this.props.lesson_plan.subject_category_name}`
                : "No category specified"}
            </CardTitle>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {this.props.lesson_plan.tutoring_job.placement_request ? (
                <a
                  href={
                    this.props.lesson_plan.tutoring_job.placement_request
                      .links.self
                  }
                  target="_blank"
                >
                  <Badge variant="warning" size="md" className="cursor-pointer font-bold">
                    In Placement <ExternalLink className="size-3"/>
                  </Badge>
                </a>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={this.onRequestPlacement}
                >
                  Request Placement <ChevronDown className="size-3.5"/>
                </Button>
              )}
              <RequestSearchButton
                tutoring_job={this.props.tutoring_job}
                ui={this.props.ui}
              />
              <VerifiedJobButton tutoring_job={this.props.tutoring_job} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Trash2 className="size-3.5"/> Delete Lesson Plan
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>Cancel</DropdownMenuItem>
                  <DropdownMenuItem variant="destructive" onSelect={this.onRemove}>Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {_.any(job_alert_requisites) ? (
            <div className="text-center">
              {_.map(job_alert_requisites, (requisite) => {
                return (
                  <Badge
                    variant="warning"
                    className="mr-2 mt-6"
                    key={requisite.name}
                  >
                    <TriangleAlert className="size-3"/>
                    {requisite.message}
                  </Badge>
                );
              })}
            </div>
          ) : null}
          <Tabs value={tab} onValueChange={this.onTabClick} className="mt-3 w-fit">
            <TabsList>
              {_.map(tabOptions, (tabOption) => (
                <TabsTrigger key={tabOption.value} value={tabOption.value}>
                  {tabOption.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </Card>
        {this.props.ui.request_placement.tutoring_job_id ===
        this.props.tutoring_job.id ? (
          <RequestPlacement
            tutoring_job={this.props.tutoring_job}
            lesson_plan={this.props.lesson_plan}
            ui={this.props.ui}
          />
        ) : null}
        {this.props.ui.tutor_searcher.tutoring_job_id ===
        this.props.tutoring_job.id ? (
          <TutorSearcher
            ui={this.props.ui}
            tutoring_job={this.props.tutoring_job}
            lesson_plan={this.props.lesson_plan}
          />
        ) : null}
        {tab === "lesson_setup" ? (
          <LessonPlanLessonSetup
            lesson_plan={this.props.lesson_plan}
            enquiry_id={this.props.enquiry_id}
            subjects={this.props.subjects}
            learners={this.props.learners}
            google_map_ui={this.props.google_map_ui}
            ui={this.props.ui}
          />
        ) : null}
        {tab === "lesson_pricing" ? (
          <LessonPlanPricing
            lesson_plan={this.props.lesson_plan}
            enquiry_id={this.props.enquiry_id}
            pricing_calculator={this.props.pricing_calculator}
            change_applied={this.props.change_applied}
          />
        ) : null}
        {tab === "placement" ? (
          <React.Fragment>
            <TutoringJobPositions
              tutoring_job={this.props.tutoring_job}
              ui={this.props.ui}
            />
            <LessonPlanMeetings
              tutoring_job={this.props.tutoring_job}
            />
          </React.Fragment>
        ) : null}
        {tab === "tutors" ? (
          <LessonPlanTutors
            tutoring_job={this.props.tutoring_job}
            lesson_plan={this.props.lesson_plan}
            enquiry_id={this.props.enquiry_id}
            tutor_results={this.props.tutor_results}
            tutor_proposals={this.props.tutor_proposals}
            ui={this.props.ui}
          />
        ) : null}
        {tab === "notes" ? (
          <LessonPlanNotes tutoring_job={this.props.tutoring_job} />
        ) : null}
        {_.any(pending_tutor_proposals) ? (
          <PendingTutorProposals
            tutoring_job_id={this.props.tutoring_job.id}
            tutor_proposals={pending_tutor_proposals}
          />
        ) : null}
      </div>
    );
  }
}

export default EnquiryLessonPlan;
